import { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '../../react-auth0-spa';
import { paths } from '../../routes';
import {
    getRawSolutionFromUrl,
    isSupportedSignupSolution,
    parseSolutionFromUrl,
    stashSignupSolution,
} from '../../common/selfServiceSolutions';
import LoadingPage from '../common/LoadingPage';
import UnsupportedSolutionScreen from './UnsupportedSolutionScreen';

// Entry gate for the self-service signup flow. Mounted at `/signup`.
//
// Branches on the `?solution=…` URL param against the
// SUPPORTED_SIGNUP allowlist (`selfServiceSolutions.ts`):
//
//   - Supported (today: only `room`) → stash the solution in
//     sessionStorage so it survives the Auth0 round-trip, set the
//     post-login target to `/onboarding`, and kick off the Auth0
//     signup redirect. The previously-handled inline behavior in
//     App.tsx's bootstrap effect is now skipped for `/signup` —
//     see the matching guard there.
//
//   - Missing or unsupported → render UnsupportedSolutionScreen.
//     We never call Auth0 in this case, so the user can't create
//     a dangling Auth0 account for a solution we can't onboard.
const SignupGate = () => {
    const { loading, isAuthenticated, loginWithRedirect } = useAuth0();

    // Read the URL params once on mount. Re-reading on every render
    // would risk diverging from the value we stash if React Router
    // ever updates `location.search` (e.g. on a programmatic replace).
    const requested = useMemo(() => getRawSolutionFromUrl(), []);
    const validated = useMemo(() => parseSolutionFromUrl(), []);
    const supported = isSupportedSignupSolution(validated);

    // We need a render gate so the unsupported screen flashes before
    // the redirect call runs (the effect below resolves the loading
    // state, which gates the loginWithRedirect call).
    const [redirecting, setRedirecting] = useState(false);

    useEffect(() => {
        if (!supported || !validated) return;
        if (loading) return;
        if (redirecting) return;

        setRedirecting(true);
        // Stash the solution so OnboardingPage can read it back —
        // sessionStorage survives because we stay on the same tab + origin.
        stashSignupSolution(validated);

        if (isAuthenticated) {
            // Already signed up — e.g. the user arrived authenticated, hit the
            // unsupported screen for a missing/unknown solution, and clicked
            // "try kolula ROOM" (→ /signup?solution=room). Re-triggering the
            // Auth0 signup is wrong here, and the old `isAuthenticated` early
            // return left an infinite spinner. Just go to onboarding with the
            // solution now stashed. Full navigation: SignupGate renders outside
            // the Router (App.tsx returns it before <BrowserRouter>), so
            // useNavigate isn't available.
            window.location.assign(paths.onboarding);
            return;
        }

        // App.tsx's PostLoginRedirect reads `postLoginTarget` from
        // sessionStorage and navigates there once the callback lands.
        window.sessionStorage.setItem('postLoginTarget', paths.onboarding);
        loginWithRedirect({
            authorizationParams: { screen_hint: 'signup' },
            // appState is preserved by the Auth0 SPA SDK across the
            // redirect. We don't rely on it (PostLoginRedirect uses
            // the sessionStorage target) but include it so a future
            // refactor that wants to switch to appState-only doesn't
            // have to retrofit this call site.
            appState: { targetUrl: paths.onboarding, signupSolution: validated },
        });
    }, [supported, validated, loading, isAuthenticated, redirecting, loginWithRedirect]);

    if (!supported) {
        return <UnsupportedSolutionScreen requested={requested} />;
    }

    // While Auth0 SDK boots / the redirect is in flight, show the
    // same neutral loading page the rest of the app uses so users
    // don't see a blank screen.
    return <LoadingPage />;
};

export default SignupGate;
